import { Button, useToggle } from '@commenda/kakashi';
import { THumanConfirmStepDefinition } from '@commenda/types';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useMemo } from 'react';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { ServiceRequestTaskLabel } from '../../labels';
import { OutputSchemaModal } from '../../modals/OutputSchemaModal';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';
import { isCurrentTimelineTask } from '../../../utils';
import MarkAsCompleteTask from '../MarkAsCompleteTask';
import { getCompletedTaskLabel } from '../getCompletedTaskLabel';
import { WorkflowHelpResourceButtons } from './WorkflowHelpResourceButtons';

export const WorkflowHumanConfirmationTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { workflowExecution } = context;
  if (!workflowExecution?.WorkflowTemplateVersion?.definition)
    console.error('Workflow definition not found');
  if (!workflowExecution?.runtime?.stepExecutions)
    console.error('Workflow runtime step executions not found');

  const outputSchemaModalVisibility = useToggle(false);

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    ) as THumanConfirmStepDefinition | undefined;
  }, [workflowExecution, task.workflowDefinitionStepId]);

  if (!workflowExecution || !stepDefinition) {
    return (
      <div className="p-4 text-center">
        <p className="text-text-error txt-small-regular">
          Failed to load workflow step information
        </p>
      </div>
    );
  }

  if (!stepDefinition.outputSchema) {
    return (
      <MarkAsCompleteTask
        task={task}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
    );
  }

  // If output schema exists, render task card with Start button
  const { role } = context;
  const isCurrentTask = isCurrentTimelineTask(task);
  const completedLabel = getCompletedTaskLabel({
    task,
    serviceRequest: context.serviceRequest,
  });

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions:
              task.pendingOn === TaskParty.CLIENT && isCurrentTask ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={outputSchemaModalVisibility.show}>
                    Start
                  </Button>
                  <WorkflowHelpResourceButtons
                    helpResources={stepDefinition.helpResources}
                  />
                </div>
              ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions:
              task.pendingOn === TaskParty.CLIENT && isCurrentTask ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={outputSchemaModalVisibility.show}>
                    Continue
                  </Button>
                  <WorkflowHelpResourceButtons
                    helpResources={stepDefinition.helpResources}
                  />
                </div>
              ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={outputSchemaModalVisibility.show}
                >
                  View
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={outputSchemaModalVisibility.show}
                  variant={
                    task.pendingOn === TaskParty.CLIENT
                      ? 'secondary'
                      : 'primary'
                  }
                >
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Start on client's behalf"
                    : 'Start'}
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button size="sm" onClick={outputSchemaModalVisibility.show}>
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Continue on client's behalf"
                    : 'Continue'}
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={outputSchemaModalVisibility.show}
                >
                  View
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.ADMIN]: {
          [TaskStatus.TO_DO]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={outputSchemaModalVisibility.show}
                  variant={
                    task.pendingOn === TaskParty.CLIENT
                      ? 'secondary'
                      : 'primary'
                  }
                >
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Start on client's behalf"
                    : 'Start'}
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: isCurrentTask ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={outputSchemaModalVisibility.show}
                >
                  {task.pendingOn === TaskParty.CLIENT
                    ? "Continue on client's behalf"
                    : 'Continue'}
                  Continue
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={outputSchemaModalVisibility.show}
                >
                  View
                </Button>
                <WorkflowHelpResourceButtons
                  helpResources={stepDefinition.helpResources}
                />
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <OutputSchemaModal
        isOpen={outputSchemaModalVisibility.value}
        onClose={outputSchemaModalVisibility.off}
        schema={stepDefinition.outputSchema}
        title={stepDefinition.name || task.title}
        description={stepDefinition.description}
        task={task}
        context={context}
      />
    </div>
  );
};
